import { useEffect, type ReactNode } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { Skeleton } from "../../design-system";
import { friendlyError, type QueryState } from "../../analytics";
import { cn } from "../../design-system/utils/cn";
import { trackApiFailed } from "../../analytics/tracking";

export interface QueryBoundaryProps<T> {
  state: QueryState<T>;
  /** Analytics discriminator for ApiRequestFailed (e.g. "loadSummary"). When
   *  set, a failed load fires the event once per distinct error. */
  origin?: string;
  /** Shown on first load; defaults to a generic skeleton. */
  skeleton?: ReactNode;
  /** Treat this resolved value as "empty" and render `empty` instead. */
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}

/* Resolves the three states of a useQuery into UI: a skeleton on first load, a
 * recoverable error message, then the content (dimmed while a refetch is in
 * flight, so changing filters never flashes an empty panel). */
export function QueryBoundary<T>({
  state,
  origin,
  skeleton,
  isEmpty,
  empty,
  children,
}: QueryBoundaryProps<T>) {
  const loadError =
    state.error && state.data === undefined ? state.error : null;

  // Fire ApiRequestFailed once per distinct load error (effect, not render, so
  // re-renders don't re-emit). No-op when `origin` is unset or analytics is off.
  useEffect(() => {
    if (origin && loadError) trackApiFailed(origin, loadError);
  }, [origin, loadError]);

  if (state.initialLoading) {
    return <>{skeleton ?? <DefaultSkeleton />}</>;
  }

  if (loadError) {
    return <ErrorState error={loadError} />;
  }

  if (state.data === undefined) return null;

  if (isEmpty?.(state.data)) {
    return <>{empty ?? <EmptyState />}</>;
  }

  return (
    <div
      className={cn(
        "transition-opacity duration-fast",
        state.loading && "opacity-60",
      )}
    >
      {children(state.data)}
    </div>
  );
}

function DefaultSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  // Code-aware messaging from the contract { error: { code } } envelope.
  const { title, message } = friendlyError(error);
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-error-border bg-error-bg p-3 text-body-sm text-error-fg">
      <WarningCircle className="mt-0.5 size-4 shrink-0" />
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-error-fg/90">{message}</div>
      </div>
    </div>
  );
}

export function EmptyState({
  message = "No data in this range.",
}: {
  message?: string;
}) {
  return (
    <div className="flex h-24 items-center justify-center text-body-sm text-fg-3">
      {message}
    </div>
  );
}
